import * as React from 'react';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { nanoid } from 'nanoid';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import { isValidSavedFilter } from '@src/utils';
import { needCheckDimensionSelector } from '@store/tempDashboard/selectors';
import { allDimensionsListSelector } from '@store/common/dimensionList/selectors';
import type { FilterRule, SavedFilter } from '../types';

export const operatorMap = {
  eq: 'equals',
  not_eq: 'not equals',
  regex: 'match',
  not_regex: 'not match',
  contains: 'contains',
  not_contains: 'not contains',
  starts_with: 'starts with',
  not_starts_with: 'not starts with',
  ends_with: 'ends with',
  not_ends_with: 'not ends with',
  exists: 'exists',
} as const;

export const disabledOperators = ['regex', 'not_regex'];

export enum DataSource {
  video = 'video',
  app = 'app',
  ads = 'ads',
}

export type Operator = keyof typeof operatorMap;

export const isAdmin = (role: string) => role === 'Admin';

export const isInvalidXMLString = (text?: string) => {
  if (!text) return false;
  let match = false;
  // Regex for all not supported characters, currently we are skipping the discouraged charaters.
  const regex = new RegExp(
    // eslint-disable-next-line no-control-regex
    /((?:[\0-\x08\x0B\f\x0E-\x1F\uFFFD\uFFFE\uFFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF]))/g,
  );
  match = regex.test(text);
  return match;
};

const ALL_CATEGORY = 'ALL';
export const generateAllCategory = () => ({
  category: ALL_CATEGORY,
  subCategory: '',
});

export const isAllCategory = (category: string) => category === ALL_CATEGORY;

export const parseRawCategory = (categoryStr: string) => {
  // Subcategory might have more than 1 slash.
  // So here is to split the category and subcategory based on the first slash
  const [main, ...rest] = String(categoryStr || '').split('/');
  return {
    category: main || '',
    subcategory: rest.join('/') || '',
  };
};

export const gerResponseErrorMessage = (e: Error) =>
  get(e, 'response.data.error', '') || get(e, 'response.data.message', '');

export const getDataSource = (): DataSource =>
  ({
    APP: DataSource.app,
    AI: DataSource.ads,
    EI: DataSource.video,
  })[CURRENT_PRODUCT_MODE] ?? DataSource.video;

export const checkInvalidDimension = (field: string, errorDataFields = [] as string[]) => {
  return !!(errorDataFields as string[]).some((it) => it.includes(field));
};

export const useCheckSavedFilterInValid = () => {
  const needCheckDimension = useSelector(needCheckDimensionSelector);
  const allDimensions = useSelector<unknown, any>(allDimensionsListSelector);

  return React.useCallback(
    (filter: SavedFilter) => needCheckDimension && !isValidSavedFilter(filter, allDimensions),
    [needCheckDimension, allDimensions],
  );
};

export const outsidePopupClassName = `outside-popup-${nanoid()}`;
export const useClickOutsideWithPopup = <T extends HTMLElement = HTMLDivElement>(
  callback: () => void,
) => {
  const containerRef = React.useRef<T>(null);
  const callbackRef = React.useRef(callback);

  callbackRef.current = callback;

  React.useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !containerRef.current ||
        containerRef.current.contains(target) ||
        target.closest(`.${outsidePopupClassName}`)
      ) {
        return;
      }

      requestAnimationFrame(() => callbackRef.current());
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, []);

  return [containerRef, outsidePopupClassName] as const;
};

useClickOutsideWithPopup.outsidePopupClassName = outsidePopupClassName;

// Convert custom tag to dimension
export const convertCustomTagToDimension = (
  dimensions: Set<string>,
  rule: FilterRule[][],
): FilterRule[][] =>
  rule.map((ruleItems) =>
    ruleItems.map((item) =>
      // If the field is TAG and the key is in filter meta, it should be converted to a normal dimension,
      // otherwise, it should be a legacy custom tag, keep it as it is.
      item.field === 'TAG' && item.key && dimensions.has(item.key)
        ? {
            field: item.key,
            display: item.display,
            op: item.op,
            value: item.value,
          }
        : item,
    ),
  );
